import { FC, useMemo } from "react";
import { Stack, Tooltip, Typography } from "@mui/material";
import { GridApi } from "ag-grid-community";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { SavingsOpportunity } from "@/services/cloudchipr.api";
import { useTotalInHeaderCalculator } from "@/components/pages/common/ag-grid/utils/hooks/useTotalInHeaderCalculator.hook";
import { KeyValueSplitChip } from "@/components/common/chips/KeyValueSplitChip";
import { money } from "@/utils/numeral/money";
import { generateTotalFromUniqueItemsByGridApiAndKey } from "@/components/pages/savings-opportunities/utils/helpers/generateTotalFromUniqueItemsByGridApiAndKey";

interface BillingExplorerSavingOpportunitiesMetaDataProps {
  gridApi: GridApi | null;
}

export const BillingExplorerSavingOpportunitiesMetaData: FC<
  BillingExplorerSavingOpportunitiesMetaDataProps
> = ({ gridApi }) => {
  const monthlySavings = useMemo(() => {
    return generateTotalFromUniqueItemsByGridApiAndKey(
      gridApi,
      "monthly_savings",
    );
  }, [gridApi]);

  const estimatedMonthlySavings =
    useTotalInHeaderCalculator<SavingsOpportunity>(
      gridApi,
      (data) => data.estimated_monthly_price,
      "sum",
    );

  const currentMonthlyPrice = useTotalInHeaderCalculator<SavingsOpportunity>(
    gridApi,
    (data) => data.current_monthly_price,
    "sum",
  );

  return (
    <Stack direction="row" gap={1} flexWrap="wrap">
      <KeyValueSplitChip
        title="Current Monthly Price"
        value={`~${money(currentMonthlyPrice ?? 0)}`}
        hideCopyIcon
      />

      <KeyValueSplitChip
        title={
          <Stack direction="row" spacing={1}>
            <Typography variant="body2" color="text.secondary">
              Monthly Savings
            </Typography>
            <Tooltip arrow title="Potential monthly savings" placement="top">
              <InfoOutlinedIcon fontSize="small" color="action" />
            </Tooltip>
          </Stack>
        }
        value={`~${money(monthlySavings ?? 0)}`}
        hideCopyIcon
      />

      <KeyValueSplitChip
        title={
          <Stack direction="row" spacing={1}>
            <Typography variant="body2" color="text.secondary">
              Estimated Cost After Savings
            </Typography>
            <Tooltip
              arrow
              title="Estimated monthly price after implementing the recommendations."
              placement="top"
            >
              <InfoOutlinedIcon fontSize="small" color="action" />
            </Tooltip>
          </Stack>
        }
        hideCopyIcon
        value={`~${money(estimatedMonthlySavings ?? 0)}`}
      />
    </Stack>
  );
};
